/**
 * Identity-broker (ZITADEL) authentication, as an alternative to MSAL/Entra.
 *
 * Why not MSAL: MSAL hardwires `response_mode=fragment` for browser flows
 * (`responseMode` is omitted from its public request types), and ZITADEL
 * double-encodes `state` in fragment mode, so the response never matches the
 * cached request and login silently loops. See zitadel/zitadel#12645. This
 * provider therefore requests **query** response mode, which round-trips
 * correctly.
 *
 * Renewal uses a refresh token (`offline_access`), never a hidden iframe:
 * iframe-based silent auth depends on third-party cookies and is being removed
 * by browsers, and brokers that forbid framing block it outright.
 */

import { User, UserManager, WebStorageStateStore } from 'oidc-client-ts';
import { OidcSettings } from './oidc-settings';
import { LoggedInMsalUser } from './app-auth';

/** Scopes requested from the broker. `offline_access` yields the refresh token. */
export const OIDC_SCOPES = ['openid', 'profile', 'email', 'offline_access'];

export class OidcProvider {
    private readonly userManager: UserManager;

    constructor(settings: OidcSettings, extraScopes: string[] = []) {
        const scopes = [...OIDC_SCOPES, ...extraScopes].join(' ');

        this.userManager = new UserManager({
            authority: settings.authority,
            client_id: settings.clientId,
            redirect_uri: window.location.origin,
            post_logout_redirect_uri: window.location.origin,
            response_type: 'code',
            // See the file header: query mode is what makes this work at all.
            response_mode: 'query',
            scope: scopes,
            // Refresh-token renewal instead of a hidden iframe.
            automaticSilentRenew: true,
            monitorSession: false,
            // Survive a page reload; matches the MSAL path's localStorage cache.
            userStore: new WebStorageStateStore({ store: window.localStorage }),
        });
    }

    /**
     * Completes a redirect callback if one is in progress.
     *
     * @returns the app state passed to `logIn`, when the callback carried one.
     */
    async handleRedirectCallback(): Promise<string | undefined> {
        const hasCode = new URLSearchParams(window.location.search).has('code');
        if (!hasCode) { return undefined; }

        const user = await this.userManager.signinRedirectCallback();
        // Strip the code/state from the address bar so a reload cannot replay it.
        window.history.replaceState({}, document.title, window.location.pathname + window.location.hash);
        return (user.state as string | undefined) ?? undefined;
    }

    /** The signed-in user, or undefined. Does not trigger interaction. */
    async getUser(): Promise<User | undefined> {
        const user = await this.userManager.getUser();
        return user && !user.expired ? user : undefined;
    }

    /** Starts an interactive login. Navigates away; does not return. */
    async logIn(appState?: string): Promise<void> {
        await this.userManager.signinRedirect({ state: appState });
    }

    /** Ends the broker session (RP-initiated logout, with `id_token_hint`). */
    async logOut(postLogoutRedirectUri?: string): Promise<void> {
        await this.userManager.signoutRedirect(
            postLogoutRedirectUri ? { post_logout_redirect_uri: postLogoutRedirectUri } : undefined
        );
    }

    /**
     * An access token, renewed via the refresh token when expired.
     *
     * @throws when no valid token can be obtained without user interaction; the
     *     caller decides how to prompt (this app opens a new tab rather than
     *     redirecting, so in-progress work is not lost).
     */
    async getAccessToken(): Promise<string> {
        let user = await this.userManager.getUser();

        if (!user || user.expired) {
            // Refresh-token grant. Throws if the refresh token is gone or revoked.
            user = await this.userManager.signinSilent();
        }

        if (!user?.access_token) {
            throw new Error('No access token available from the identity broker');
        }
        return user.access_token;
    }

    /** User details in the shape the rest of the app already consumes. */
    async getLoggedInUserDetails(): Promise<LoggedInMsalUser | undefined> {
        const user = await this.getUser();
        if (!user) { return undefined; }

        const profile = user.profile;
        const email = profile.email ?? profile.preferred_username;
        const fullName = profile.name ?? email ?? profile.sub;

        return {
            userId: profile.sub,
            email,
            name: profile.preferred_username ?? profile.name,
            fullName,
        };
    }
}
